import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ClientContractFieldResponse } from '@interfaces/companys/contracts/responses/client-contract.response';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { ClientContractsHttpService } from '@services/http/companys/client-contracts-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { Subscription, finalize } from 'rxjs';
import { backendErrorMessage } from '../../../../partner/utils/partner-contract.utils';
import {
  ClientContractFieldsForm,
  isClientContractFieldOutOfDefault,
} from '../../utils/client-contract-fields-form';
import { formatClientContractValue } from '../../utils/client-contract.utils';

/**
 * Campos de um contrato de cliente (card 3643): os campos de valor do catálogo do tipo, com a dica
 * do padrão e a etiqueta "Fora do padrão", e os blocos opcionais do GA (Grupo econômico, com a lista
 * de empresas, e Exceção de exclusividade, com a instituição). Usado pela modal Gerar contrato e pelo
 * painel "Campos do contrato" do editor; o estado mora em {@link ClientContractFieldsForm}.
 *
 * Não calcula nada: por extenso e taxa mensal saem no texto, renderizado pelo backend.
 */
@Component({
  selector: 'app-client-contract-fields-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
  templateUrl: './client-contract-fields-form.component.html',
  styleUrls: ['./client-contract-fields-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientContractFieldsFormComponent {
  private readonly contractsService = inject(ClientContractsHttpService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  readonly form = input.required<ClientContractFieldsForm>();
  /** Empresa do contrato: de onde vêm as empresas do grupo econômico para pré-preencher. */
  readonly companyId = input<string | null>(null);
  /**
   * Só leitura: esconde os botões e trava os interruptores. Os campos travam pelo próprio formulário
   * (`root.disable()`, feito por quem é dono do estado); `[disabled]` junto de `[formControl]` geraria
   * o aviso do Angular de "disabled com reactive forms".
   */
  readonly readonly = input(false);
  /** `double`: dois campos por linha (modal); `single`: um por linha (painel lateral). */
  readonly layout = input<'single' | 'double'>('single');

  /** Sobe a cada digitação/troca de bloco: as etiquetas "Fora do padrão" leem os valores de novo. */
  private readonly changeTick = signal(0);
  readonly loadingGroupCompanies = signal(false);

  readonly groups = computed(() => {
    this.form().revision();
    return this.form().groups();
  });
  readonly blocks = computed(() => {
    this.form().revision();
    return this.form().catalog.blocks;
  });
  readonly economicGroupEnabled = computed(() => {
    this.changeTick();
    this.form().revision();
    return this.form().economicGroupEnabled.value;
  });
  readonly exclusivityEnabled = computed(() => {
    this.changeTick();
    this.form().revision();
    return this.form().exclusivityEnabled.value;
  });
  readonly groupCompanyRows = computed(() => {
    this.changeTick();
    this.form().revision();
    // Cópia: o mesmo array mutado não dispararia a troca no `@for`.
    return [...this.form().groupCompanies.controls];
  });

  constructor() {
    let subscription: Subscription | null = null;
    effect(onCleanup => {
      const form = this.form();
      subscription?.unsubscribe();
      subscription = form.changes$.subscribe(() => this.changeTick.update(value => value + 1));
      onCleanup(() => subscription?.unsubscribe());
    });
  }

  /**
   * Dica sob o campo: "Vazio = padrão 2,00%" (o backend grava o padrão quando o campo vai vazio),
   * "Obrigatório para finalizar" ou a dica do catálogo.
   */
  defaultHint(field: ClientContractFieldResponse): string {
    const parts: string[] = [];
    if (field.defaultValue !== null && field.defaultValue !== undefined) {
      parts.push(`Vazio = padrão ${formatClientContractValue(field.defaultValue, field.kind)}`);
    } else if (field.required) {
      parts.push('Obrigatório para finalizar');
    }
    if (field.hint) parts.push(field.hint);
    return parts.join(' · ');
  }

  isOutOfDefault(field: ClientContractFieldResponse, value: number | null): boolean {
    this.changeTick();
    this.form().revision();
    return isClientContractFieldOutOfDefault(field, value);
  }

  blockLabel(key: string): string {
    return this.blocks().find(block => block.key === key)?.label ?? '';
  }

  blockDescription(key: string): string {
    return this.blocks().find(block => block.key === key)?.description ?? '';
  }

  toggleEconomicGroup(enabled: boolean): void {
    const form = this.form();
    // Alterado antes do valor: quem escuta o `valueChanges` (régua de "alterado") já lê o dirty.
    form.economicGroupEnabled.markAsDirty();
    form.economicGroupEnabled.setValue(enabled);
    // Ao ligar com a lista vazia, já sugere as empresas do grupo econômico cadastrado.
    if (enabled && !form.groupCompanies.length) this.loadGroupCompanies(false);
  }

  toggleExclusivity(enabled: boolean): void {
    const form = this.form();
    // Alterado antes do valor: quem escuta o `valueChanges` (régua de "alterado") já lê o dirty.
    form.exclusivityEnabled.markAsDirty();
    form.exclusivityEnabled.setValue(enabled);
  }

  addGroupCompany(): void {
    this.form().addGroupCompany();
  }

  removeGroupCompany(index: number): void {
    this.form().removeGroupCompany(index);
  }

  /**
   * Busca as outras empresas do grupo econômico da empresa (`economic-group-companies`). Com a lista
   * já preenchida, pergunta antes de trocar.
   */
  loadGroupCompanies(askBeforeReplace = true): void {
    const companyId = this.companyId();
    if (!companyId || this.loadingGroupCompanies()) return;

    if (askBeforeReplace && this.form().groupCompanies.length) {
      this.confirmationService
        .confirm(
          'Trocar a lista pelas empresas do grupo econômico?',
          'As empresas digitadas aqui serão substituídas pelas do grupo econômico cadastrado na plataforma.',
          'default',
          'Cancelar',
          'Trocar'
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(confirmed => {
          if (confirmed) this.loadGroupCompanies(false);
        });
      return;
    }

    this.loadingGroupCompanies.set(true);
    this.contractsService
      .getEconomicGroupCompanies(companyId)
      .pipe(
        finalize(() => this.loadingGroupCompanies.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: companies => {
          if (!companies.length) {
            this.toast.showToastInfo(
              'A empresa não está em nenhum grupo econômico',
              'Digite as empresas do grupo na lista, com nome e CNPJ, ou cadastre o grupo em Gerenciamento › Grupos Econômicos.'
            );
            if (!this.form().groupCompanies.length) this.form().addGroupCompany();
            return;
          }
          this.form().replaceGroupCompanies(companies);
        },
        error: error =>
          this.toast.showToastError(
            'Não foi possível buscar as empresas do grupo econômico',
            backendErrorMessage(error) || 'Digite as empresas na lista ou tente de novo.'
          ),
      });
  }
}
